import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  eatingWaySettingActions,
  selectEatingWaySettingData,
  selectEatingWaySettingDraggingItemId,
  selectEatingWaySettingSelectedDroppableId,
  selectEatingWaySettingSelectedItemIdMap,
  selectEatingWaySettingSelectedItemIds,
} from 'features/EatingWay/eatingWaySettingSlice';
import { EatingWaySettingTimeFrame } from 'features/EatingWay/models';
import React, { useContext } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Form } from 'react-bootstrap';
import { FieldArrayWithId, useFieldArray } from 'react-hook-form';
import { PRIMARY_BUTTON } from '../../../../utils';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils/dragutils/utils';
import { EatingWaySettingContext, FormValues } from '../EatingWaySettingModal';

export interface EatingWaySettingRowProps {
  rowData: FieldArrayWithId<FormValues, 'eatingWaySettings', 'key'>;
  rowIndex: number;
}

function EatingWaySettingRow({ rowData, rowIndex }: EatingWaySettingRowProps) {
  const dispatch = useAppDispatch();

  // selector
  const data = useAppSelector(selectEatingWaySettingData);
  const selectedDroppableId = useAppSelector(selectEatingWaySettingSelectedDroppableId);
  const selectedItemIds = useAppSelector(selectEatingWaySettingSelectedItemIds);
  const selectedItemIdMap = useAppSelector(selectEatingWaySettingSelectedItemIdMap);
  const draggingItemId = useAppSelector(selectEatingWaySettingDraggingItemId);

  const eatingWaySettingContext = useContext(EatingWaySettingContext);
  const control = eatingWaySettingContext.control;
  const columnWidth = eatingWaySettingContext.columnWidth;
  const timeframeColumnWidth = eatingWaySettingContext.timeframeColumnWidth;
  const totalRowWidth = eatingWaySettingContext.totalRowWidth;
  const timeFrameSource = eatingWaySettingContext.timeFrameSource;

  const draggableId = `${rowData.eating_way_id}-${rowData.menu_book_id}-${rowData.course_id}`;
  const droppableId = `root`;

  const isSelected: boolean = droppableId === selectedDroppableId && Boolean(selectedItemIdMap[draggableId]);
  const isGhosting: boolean = isSelected && Boolean(draggingItemId) && draggingItemId !== draggableId;
  const selectionCount = selectedItemIds.length;

  const { fields, remove, append, update } = useFieldArray({
    control,
    name: `eatingWaySettings.${rowIndex}.time_frames`,
  });

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input')) {
      event.preventDefault();
    }

    // (event.currentTarget as HTMLDivElement).focus();
    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an checkbox
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input') && !targetElement.classList.contains('dan-btn-expand')) {
      event.preventDefault();
    }

    // props.toggleSelectionInGroup(props.task.id);
    dispatch(eatingWaySettingActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    // const { task, toggleSelection, toggleSelectionInGroup, multiSelectTo } = props;
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(
        eatingWaySettingActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId })
      );
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      // multiSelectTo(task.id);
      dispatch(
        eatingWaySettingActions.multiSelectTo({
          arg: {
            data,
            selectedItemIds,
            droppableId: droppableId,
          },
          newItemId: draggableId,
        })
      );
      return;
    }

    dispatch(eatingWaySettingActions.toggleSelection({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  function handleSelectedTimeFrameChanged(timeFrameId: number) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const foundIndex = fields.findIndex((x) => x.time_frame_id === timeFrameId);

      if (checked) {
        if (foundIndex < 0) {
          // append
          const newTimeFrame: EatingWaySettingTimeFrame = {
            time_frame_id: timeFrameId,
          };
          append(newTimeFrame);
        } else {
          // update
          const newTimeFrame = { ...fields[foundIndex] };
          newTimeFrame.time_frame_id = timeFrameId;
          update(foundIndex, newTimeFrame);
        }
      } else {
        // remove
        remove(foundIndex);
      }
    };
  }

  return (
    <Draggable draggableId={`${draggableId}`} index={rowIndex}>
      {(provided, snapshot) => {
        const shouldShowSelection: boolean = snapshot.isDragging && selectionCount > 1;
        const style = eatingWaySettingContext.getItemStyle(
          snapshot.isDragging,
          provided.draggableProps.style,
          isSelected,
          isGhosting
        );
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...style,
              width: timeFrameSource === undefined || timeFrameSource.length <= 3 ? 774 : totalRowWidth + 2,
            }}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <div className="dan-tr">
              <div
                className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                style={{
                  width: columnWidth[0],
                  backgroundColor: style.background,
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                }}
              >
                <Form.Check
                  value={`${rowIndex}`}
                  tabIndex={-1}
                  className="chk-ews-selection custom-checkbox"
                  onChange={eatingWaySettingContext.onSelectedRowChanged}
                ></Form.Check>
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0"
                style={{
                  width: columnWidth[1],
                  backgroundColor: style.background,
                  position: 'sticky',
                  left: columnWidth[0],
                  zIndex: 10,
                }}
              >
                <span>{rowData.menu_book_name}</span>
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0"
                style={{
                  width: columnWidth[2],
                  backgroundColor: style.background,
                  position: 'sticky',
                  left: columnWidth[0] + columnWidth[1],
                  borderRight: '1px solid var(--bs-border-color)',
                  zIndex: 10,
                }}
              >
                <span>{rowData.course_name}</span>
              </div>

              {timeFrameSource &&
                timeFrameSource.map(function (timeFrame) {
                  return (
                    <div
                      key={timeFrame.id}
                      className="dan-td flex-fill justify-content-center"
                      style={{ width: timeframeColumnWidth }}
                    >
                      <Form.Check
                        className="custom-checkbox"
                        value={`${timeFrame.id}`}
                        tabIndex={-1}
                        defaultChecked={rowData.time_frames.some((x) => x.time_frame_id === timeFrame.id)}
                        onChange={handleSelectedTimeFrameChanged(timeFrame.id)}
                      ></Form.Check>
                    </div>
                  );
                })}

              {shouldShowSelection ? <div className="selection-count">{selectionCount}</div> : null}
            </div>
          </div>
        );
      }}
    </Draggable>
  );
}

export default EatingWaySettingRow;
